import api from './api';
import { parseFlowProjectId, safeFilmMediaUrl, type FilmProduct } from './films';

export type ScreenplayStatus = 'draft' | 'analyzing' | 'matches_ready' | 'preparing' | 'review' | 'approved'
  | 'running' | 'paused' | 'complete' | 'blocked' | 'attention';
export type ScreenplayPlanner = 'gemini' | 'codex';
export type ScreenplayDirectionProfile = 'cinematic-luxury-v1';
export interface CinematicSkillReceipt {
  profile: ScreenplayDirectionProfile; version: 1; provider: ScreenplayPlanner | 'notebooklm'; sha256: string;
  documents: { id: string; sha256: string }[];
}
export interface CinematicDirection {
  sceneNumber: number; generationMode: 'first-frame' | 'first-last'; startFramePrompt: string; endFramePrompt: string;
  transitionIn: string; transitionOut: string; allowDialogueOverlap?: true; longTakeReason?: string;
  shots: { startMs: number; endMs: number; camera: 'wide' | 'medium' | 'close-up' | 'over-shoulder' | 'insert';
    movement: string; visibleNames: string[]; action: string; composition?: string; blocking?: string; environment?: string; performance?: string; narrativePurpose?: string }[];
  dialogueCues: { dialogueIndex: number; startMs: number; endMs: number; delivery: 'onscreen' | 'offscreen' | 'recorded' }[];
}
export interface ScreenplayInput {
  title?: string; draft: string; productFolder: string; characterFolder: string; material?: string;
  planner?: ScreenplayPlanner;
  directionProfile?: ScreenplayDirectionProfile;
}
export type ScreenplayNormalizedInput = Required<Omit<ScreenplayInput, 'planner' | 'directionProfile'>> & Pick<ScreenplayInput, 'planner' | 'directionProfile'>;
export interface ScreenplayCreateInput extends ScreenplayInput { id: string }
export interface ScreenplayCreationAttempt { readonly input: Readonly<ScreenplayCreateInput> }
export interface ScreenplayTask {
  kind: 'analyze' | 'prepare' | 'run'; state: 'queued' | 'running' | 'paused' | 'complete' | 'failed';
  step: string; updatedAt: string;
}
export interface ScreenplayBlocker { code: string; message: string }
export type ScreenplayAssetKind = 'product' | 'character';
export interface ScreenplayAsset {
  id: string; name: string; kind: ScreenplayAssetKind; relativePath: string;
  mime: 'image/png' | 'image/jpeg' | 'image/webp'; size: number; sha256: string;
}
export interface ScreenplayMatch {
  name: string; kind: ScreenplayAssetKind; status: 'matched' | 'missing' | 'ambiguous'; assetIds: string[];
  candidateAssetIds?: string[];
}
export interface ScreenplaySelection { name: string; kind: ScreenplayAssetKind; assetIds: string[]; generate?: true }
export type ScreenplayProductAssignment =
  | { productName: string; productNames?: never }
  | { productNames: string[]; productName?: never };
export function screenplayEpisodeProductNames(episode: ScreenplayProductAssignment): string[] {
  if (Object.prototype.hasOwnProperty.call(episode, 'productName') === Object.prototype.hasOwnProperty.call(episode, 'productNames')) return [];
  const names = episode.productNames ?? [episode.productName];
  return Array.isArray(names) && names.length >= 1 && names.length <= 8
    && names.every((name): name is string => typeof name === 'string' && name.length <= 100 && !!name.trim())
    && new Set(names).size === names.length ? names as string[] : [];
}
export interface ScreenplayAnalysis {
  version: 2; title: string; language: string; orientation: 'VERTICAL';
  characters: { name: string; voiceOnly: boolean; description: string }[];
  products: { name: string; episodes: number[] }[];
  episodes: (ScreenplayProductAssignment & { number: number; title: string; durationSec: number; characterNames: string[]; sourceText: string })[];
}
export interface ScreenplayBinding { name: string; kind: ScreenplayAssetKind; sourceIds: string[]; generate?: true }
export interface ScreenplayEntity {
  name: string; entityType: 'character' | 'creature' | 'location' | 'visual_asset'; description: string;
  voiceDescription?: string; voiceOnly?: boolean; sourceEvidenceIds: string[];
}
export interface ScreenplayScene {
  number: number; title: string; sourceBeat: string; durationSec: number; sourceDurationSec: 4 | 6 | 8;
  prompt: string; videoPrompt: string; entityNames: string[]; dialogue: { speaker: string; text: string }[]; productPlacement: boolean;
  cinematicDirection?: CinematicDirection;
}
export type ScreenplayEpisode = ScreenplayProductAssignment & {
  number: number; title: string; durationSec: number; hook: string; continuityFromPrevious: string;
  ending: 'cliffhanger' | 'finale'; nextEpisodePromise: string; caption: string; affiliateDisclosure: string;
  publishingChecklist: { commercialDisclosure: true; aiLabel: true }; scenes: ScreenplayScene[];
};
export interface ScreenplayPlan {
  version: 2; title: string; story: string; material: string; language: string; orientation: 'VERTICAL'; draftSha256: string;
  directionProfile?: ScreenplayDirectionProfile;
  entities: ScreenplayEntity[]; products: FilmProduct[]; episodes: ScreenplayEpisode[];
  audit: { policyVersion: string; policySha256: string;
    rules: { id: string; status: 'PASS'; application: string; sceneRefs: string[] }[]; blockingFindings: string[] };
}
export interface ScreenplayProvenance {
  notebookId: string; rulesSourceId: string; contextSourceId: string; sourceIds: string[];
  policyVersion: string; policySha256: string; conversationId: string; draftSha256: string;
  cinematicSkills?: CinematicSkillReceipt;
}
export interface ScreenplayEpisodeOutput {
  mergedUrl: string; clipCount: number; durationSec: number; opencutProjectId: string; opencutRenderId: string;
}
export interface ScreenplayOutput extends ScreenplayEpisodeOutput { episode: number }
export interface ScreenplayRegenerationSelection { episodeNumber: number; sceneNumbers: number[]; requestId: string }
export interface ScreenplayRegenerationCommand extends ScreenplayRegenerationSelection { planHash: string; confirmCredits: true }
export interface ScreenplayEpisodeExecution {
  number: number; videoId?: string; sceneIds: string[]; stage?: 'references' | 'images' | 'videos' | 'export';
  videoModel?: 'veo' | 'omni_flash';
  videoTake?: { number: number; requestId: string; sceneNumbers: number[] };
  previousTakes?: { number: number; requestId?: string; sceneNumbers: number[];
    mediaIds: { sceneId: string; mediaId: string }[]; output: ScreenplayEpisodeOutput }[];
  requests?: { id: string; type: string; status: string; modelFamily?: 'veo' | 'omni_flash'; take?: number }[]; output?: ScreenplayEpisodeOutput;
}
export interface ScreenplayReferenceImage {
  entityName: string; entityId: string; mediaId: string; relativePath: string;
  sha256: string; mime: ScreenplayAsset['mime']; size: number;
  source: 'original' | 'generated'; originalAssetId?: string;
}
export interface ScreenplayExecution {
  flowProjectId: string; projectId?: string;
  references?: ScreenplayReferenceImage[]; referenceDirectory?: string;
  entities?: { name: string; id: string; mediaId?: string; originalAssetId?: string }[];
  episodes?: ScreenplayEpisodeExecution[];
}
export interface ScreenplayRecord extends ScreenplayNormalizedInput {
  version: 2; id: string; createdAt: string; updatedAt: string; status: ScreenplayStatus;
  task?: ScreenplayTask; blocker?: ScreenplayBlocker;
  analysis?: ScreenplayAnalysis; matches?: ScreenplayMatch[]; assets?: ScreenplayAsset[]; selections?: ScreenplaySelection[];
  analysisProvider?: ScreenplayPlanner;
  loadedSkills?: { id: string; sha256: string }[];
    cinematicSkills?: CinematicSkillReceipt;
  notebook?: { notebookId: string; rulesSourceId: string; policyVersion: string; policySha256: string; verified: true };
  bindings?: ScreenplayBinding[]; plan?: ScreenplayPlan; provenance?: ScreenplayProvenance; planHash?: string;
  approved?: { planHash: string; approvedAt: string }; execution?: ScreenplayExecution; outputs?: ScreenplayOutput[];
  runEpisodeNumbers?: number[];
}
export interface ScreenplaySummary {
  version: 2; id: string; title: string; status: ScreenplayStatus; createdAt: string; updatedAt: string;
  planner?: ScreenplayPlanner;
  directionProfile?: ScreenplayDirectionProfile;
  episodeCount: number; assetCount: number; outputCount: number; task?: ScreenplayTask; blocker?: ScreenplayBlocker;
}
export interface ScreenplayPolicy { version: string; sha256: string; text: string; ruleIds: string[] }

export const SCREENPLAY_DRAFT_BYTES = 100 * 1024;
export const SCREENPLAY_IMAGE_BYTES = 5 * 1024 * 1024;
export const SCREENPLAY_TIMEOUT = 20_000;
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
const SHA256 = /^[a-f0-9]{64}$/;
const BAD_UNICODE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/u;
export const screenplayBytes = (value: string): number => new TextEncoder().encode(value).length;

export function pasteScreenplayDraft(draft: string, clipboard: string, start: number, end: number): string {
  const rawOffset = (visibleOffset: number) => {
    let offset = 0;
    for (let visible = 0; visible < visibleOffset && offset < draft.length; visible++, offset++) {
      if (draft[offset] === '\r' && draft[offset + 1] === '\n') offset++;
    }
    return offset;
  };
  return draft.slice(0, rawOffset(start)) + clipboard + draft.slice(rawOffset(end));
}

function requireValue(value: unknown, message: string): asserts value {
  if (!value) throw new Error(message);
}
function validateEpisodeNumbers(raw: unknown): number[] {
  requireValue(Array.isArray(raw) && raw.length > 0 && raw.length <= 8
    && raw.every(number => Number.isInteger(number) && number >= 1 && number <= 8)
    && new Set(raw).size === raw.length, 'Select one or more distinct episodes from the approved plan.');
  return [...raw].sort((left, right) => left - right);
}
function validateSceneNumbers(raw: unknown): number[] {
  requireValue(Array.isArray(raw) && raw.length > 0 && raw.length <= 12
    && raw.every(number => Number.isInteger(number) && number >= 1 && number <= 12)
    && new Set(raw).size === raw.length, 'Select one or more distinct scenes from the completed episode.');
  return [...raw].sort((left, right) => left - right);
}
export function validateScreenplayRegeneration(command: ScreenplayRegenerationCommand): ScreenplayRegenerationCommand {
  requireValue(command?.confirmCredits === true, 'Confirm that regeneration uses provider credits.');
  return { planHash: sha256(command.planHash), episodeNumber: validateEpisodeNumbers([command.episodeNumber])[0],
    sceneNumbers: validateSceneNumbers(command.sceneNumbers), requestId: uuid(command.requestId), confirmCredits: true };
}
function uuid(value: string): string {
  requireValue(typeof value === 'string' && UUID.test(value), 'A valid lowercase screenplay UUID is required.');
  return value;
}
function boundedBody<Value>(value: Value): Value {
  requireValue(screenplayBytes(JSON.stringify(value)) <= 256 * 1024, 'Screenplay request exceeds 256 KiB UTF-8.');
  return value;
}
function imageFolder(value: string): string {
  requireValue(typeof value === 'string' && value.trim() && value.length <= 1000
    && !/[\p{Cc}\p{Cf}\\]/u.test(value) && !BAD_UNICODE.test(value) && !/^[a-z][a-z\d+.-]*:/i.test(value)
    && !value.startsWith('//') && value.split('/').every(part => !part.startsWith('.')
      && !['secrets', 'node_modules'].includes(part.toLowerCase())),
  'Image folder must be a local path of at most 1000 characters, without hidden or restricted directories.');
  return value;
}
export function validateScreenplayInput(input: ScreenplayInput): ScreenplayNormalizedInput {
    requireValue(input.directionProfile === undefined || input.directionProfile === 'cinematic-luxury-v1', 'Unknown cinematic direction profile.');
  requireValue(input.planner === undefined || input.planner === 'gemini' || input.planner === 'codex',
    'Planner must be Gemini or Codex.');
  const draft = input.draft;
  requireValue(typeof draft === 'string' && draft.trim() && screenplayBytes(draft) <= SCREENPLAY_DRAFT_BYTES
    && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(draft) && !BAD_UNICODE.test(draft),
  'Full screenplay is required, without invalid characters, and limited to 100 KiB UTF-8.');
  const heading = /^ {0,3}#{1,6}[\t ]+([^\r\n]+)/mu.exec(draft)?.[1].replace(/[\t ]+#+[\t ]*$/, '').trim();
  const derivedTitle = heading?.slice(0, 200).replace(/[\ud800-\udbff]$/u, '');
  const title = input.title ?? (derivedTitle || 'Untitled screenplay');
  requireValue(typeof title === 'string' && title.trim() && title.length <= 200
    && !/[\u0000-\u001f\u007f]/u.test(title) && !BAD_UNICODE.test(title), 'Title must be one line of at most 200 characters.');
  const material = input.material ?? 'realistic';
  requireValue(typeof material === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/.test(material),
    'Material must be a single token of at most 64 letters, digits, underscores or hyphens.');
  return boundedBody({ title, draft, productFolder: imageFolder(input.productFolder), characterFolder: imageFolder(input.characterFolder), material,
    ...(input.planner === undefined ? {} : { planner: input.planner }),
    ...(input.directionProfile === undefined ? {} : { directionProfile: input.directionProfile }) });
}
export function screenplayCreationAttempt(input: ScreenplayInput, previous?: ScreenplayCreationAttempt,
  newId: () => string = () => crypto.randomUUID()): ScreenplayCreationAttempt {
  const value = validateScreenplayInput(input);
  if (previous) {
    requireValue((value.planner ?? 'gemini') === (previous.input.planner ?? 'gemini')
      && value.directionProfile === previous.input.directionProfile
      && (Object.keys(value) as (keyof ScreenplayInput)[]).every(key => key === 'planner' || value[key] === previous.input[key]),
      'This creation attempt is locked to its original input. Observe the saved UUID before creating another draft.');
    return previous;
  }
  return Object.freeze({ input: Object.freeze(boundedBody({ id: uuid(newId()), ...value })) });
}
export function screenplayRecoveryKey(username: unknown): string | null {
  return typeof username === 'string' && username.length > 0 && username.length <= 200 && !BAD_UNICODE.test(username)
    ? `studio-screenplay-pending:v1:${encodeURIComponent(username)}` : null;
}
export function serializeScreenplayCreation(attempt: ScreenplayCreationAttempt): string {
  return JSON.stringify(boundedBody({ version: 1, input: { id: uuid(attempt.input.id), ...validateScreenplayInput(attempt.input) } }));
}
export function restoreScreenplayCreation(serialized: string | null): ScreenplayCreationAttempt | undefined {
  if (typeof serialized !== 'string' || serialized.length > 256 * 1024 || screenplayBytes(serialized) > 256 * 1024) return;
  try {
    const saved = JSON.parse(serialized);
    const fields = ['id', 'title', 'draft', 'productFolder', 'characterFolder', 'material'];
    if (!saved || typeof saved !== 'object' || saved.version !== 1 || Object.keys(saved).length !== 2
      || !saved.input || typeof saved.input !== 'object'
      || Object.keys(saved.input).some(field => !fields.includes(field) && !['planner', 'directionProfile'].includes(field))
      || !fields.every(field => typeof saved.input[field] === 'string')
      || ('planner' in saved.input && !['gemini', 'codex'].includes(saved.input.planner))
      || ('directionProfile' in saved.input && saved.input.directionProfile !== 'cinematic-luxury-v1')) return;
    return screenplayCreationAttempt(saved.input, undefined, () => saved.input.id);
  } catch { return; }
}
export function screenplayPath(id: string): string { return `/films/screenplays/${uuid(id)}`; }
function sha256(value: string): string {
  requireValue(typeof value === 'string' && SHA256.test(value), 'A valid SHA256 asset or plan hash is required.');
  return value;
}
export function validateScreenplaySelections(record: ScreenplayRecord, selections: ScreenplaySelection[]): ScreenplaySelection[] {
  const message = 'Select one available cast original, explicitly generate missing visible cast, or select 1-6 product images from one catalog folder.';
  requireValue(record.analysis && Array.isArray(record.matches) && Array.isArray(record.assets)
    && Array.isArray(selections) && selections.length <= 16, message);
  const identities = new Set<string>(), originals = new Set<string>(), hashes = new Map<string, string>();
  const result = selections.map(selection => {
    const match = record.matches!.find(item => item.name === selection.name && item.kind === selection.kind);
    const key = JSON.stringify([selection.kind, selection.name]);
    requireValue(match && ['product', 'character'].includes(selection.kind) && !identities.has(key)
      && Array.isArray(selection.assetIds), message);
    identities.add(key);
    if (selection.generate === true) {
      requireValue(screenplayCanGenerateCharacter(record, match) && selection.assetIds.length === 0, message);
      return { kind: match.kind, name: match.name, assetIds: [], generate: true as const };
    }
    requireValue(selection.generate === undefined && selection.assetIds.length >= 1
      && selection.assetIds.length <= (selection.kind === 'product' ? 6 : 1)
      && (match.candidateAssetIds === undefined || match.kind === 'product'), message);
    const lists = [match.assetIds, ...(match.candidateAssetIds === undefined ? [] : [match.candidateAssetIds])];
    requireValue(lists.every(ids => Array.isArray(ids) && ids.length <= 128 && new Set(ids).size === ids.length
      && ids.every(id => typeof id === 'string' && SHA256.test(id)
        && record.assets!.some(asset => asset.id === id && asset.kind === match.kind))), message);
    const allowed = new Set(lists.flat()), folders = new Set<string>();
    for (const assetId of selection.assetIds) {
      const asset = record.assets!.find(item => item.id === assetId);
      requireValue(typeof assetId === 'string' && SHA256.test(assetId) && allowed.has(assetId)
        && asset && asset.kind === selection.kind && !originals.has(assetId) && SHA256.test(asset.sha256), message);
      requireValue(!hashes.has(asset.sha256) || hashes.get(asset.sha256) === key,
        'The same original image cannot be selected for different products or characters.');
      originals.add(assetId); hashes.set(asset.sha256, key); folders.add(asset.name);
    }
    requireValue(selection.kind !== 'product' || folders.size === 1, message);
    return { kind: match.kind, name: match.name, assetIds: [...selection.assetIds] };
  });
  requireValue(originals.size <= 48, 'Select at most 48 original images.');
  return boundedBody(result);
}
export function screenplayCanGenerateCharacter(record: ScreenplayRecord, match: ScreenplayMatch): boolean {
  return match.kind === 'character' && match.status === 'missing' && Array.isArray(match.assetIds) && !match.assetIds.length
    && match.candidateAssetIds === undefined
    && !!record.analysis?.characters.some(character => character.name === match.name && character.voiceOnly === false);
}
export function screenplaySelectionsComplete(record: ScreenplayRecord, selections = record.selections ?? []): boolean {
  try {
    const valid = validateScreenplaySelections(record, selections);
    const required = [...record.analysis!.products.map(product => ({ kind: 'product', name: product.name })),
      ...record.analysis!.characters.filter(character => !character.voiceOnly).map(character => ({ kind: 'character', name: character.name }))];
    return !!required.length && record.matches?.length === required.length && valid.length === required.length
      && required.every(match => valid.some(selection => selection.name === match.name && selection.kind === match.kind));
  } catch { return false; }
}

const ERRORS: Record<string, string> = {
  SCREENPLAY_OWNER_REQUIRED: 'A verified Studio principal is required.',
  SCREENPLAY_CINEMATIC_UNAVAILABLE: 'The cinematic profile requires Omni Flash with verified first and end image frames. Check the active Flow bridge.',
  SCREENPLAY_INPUT_INVALID: 'Check the screenplay fields and size limits.',
  SCREENPLAY_NOT_FOUND: 'Screenplay film not found.',
  SCREENPLAY_CONFLICT: 'This film ID already exists with different inputs. Keep the original creation attempt.',
  SCREENPLAY_DRAFT_LOCKED: 'Active or executed films cannot change inputs. Pause first, or create a new draft.',
  SCREENPLAY_REVISION_LIMIT: 'Ten input revisions reached. Create a new draft; existing sources are retained.',
  SCREENPLAY_STORAGE_UNSAFE: 'Screenplay storage is unavailable or unsafe.',
  SCREENPLAY_STORAGE_LIMIT: 'Screenplay storage limit reached. Existing evidence is retained.',
  SCREENPLAY_SCENE_SELECTION_INVALID: 'Select one or more distinct scenes from a completed approved episode.',
  SCREENPLAY_TAKE_LIMIT: 'Eight video takes reached for this episode. Earlier clips and exports are retained.',
  SCREENPLAY_BUSY: 'Current work is still finishing. Refresh its saved state before acting.',
  SCREENPLAY_ANALYSIS_REQUIRED: 'Analyze the current screenplay before selecting images.',
  SCREENPLAY_SELECTION_INVALID: 'Select originals from one product folder or available cast candidates, or explicitly generate missing visible cast.',
  SCREENPLAY_MATCHES_REQUIRED: 'Select originals for every product and available visible character, or generate missing visible cast from the approved description.',
  SCREENPLAY_DUPLICATE_IMAGE: 'The same original image cannot belong to different products or character bindings.',
  SCREENPLAY_PREPARATION_REQUIRED: 'Prepare a source-bound plan before approval.',
  SCREENPLAY_APPROVAL_REQUIRED: 'Approve the current plan before running or resuming.',
  SCREENPLAY_HASH_MISMATCH: 'The reviewed plan hash has changed. Refresh and review the current plan.',
  SCREENPLAY_POLICY_UNAVAILABLE: 'The canonical screenplay policy is unavailable.',
  SCREENPLAY_POLICY_MISMATCH: 'The saved plan does not match the current policy. Revise and prepare again.',
  SCREENPLAY_RUNTIME_UNAVAILABLE: 'The screenplay runtime is unavailable. No run was queued.',
  SCREENPLAY_MODEL_ACCESS_DENIED: 'Google denied access to the selected video model. Confirm model access in Google Flow or explicitly choose another model; saved images and references are retained.',
  SCREENPLAY_PROJECT_CONFLICT: 'This Google Flow project belongs to another film. Use a dedicated project UUID.',
  SCREENPLAY_EFFECT_UNCERTAIN: 'An effect outcome is uncertain. Inspect the saved operation and upstream state; do not resubmit.',
  SCREENPLAY_WORKER_UNCERTAIN: 'A previous worker may still be active. Operator inspection is required before continuing.',
  SCREENPLAY_ANALYSIS_FAILED: 'Selected planner analysis failed. No analysis was accepted.',
  SCREENPLAY_ANALYSIS_MODEL_UNAVAILABLE: 'Codex rejected the configured model. Correct the Codex model configuration before explicitly retrying.',
  SCREENPLAY_ANALYSIS_AUTH_REJECTED: 'Planner authentication was rejected. Reconnect the provider before explicitly retrying.',
  SCREENPLAY_ANALYSIS_QUOTA_REJECTED: 'The planner rejected the request due to quota or rate limits. Resolve the limit before explicitly retrying.',
  SCREENPLAY_SCRIPT_INVALID: 'The draft or completed analysis failed screenplay validation. No analysis was accepted.',
  SCREENPLAY_MULTIPLE_PRODUCTS: 'The previous attempt used single-product rules. Multiple products are now supported. Analyze again to validate this unchanged draft under the current rules. No request was sent automatically.',
  SCREENPLAY_SKILLS_UNAVAILABLE: 'Required screenplay skill context is unavailable or invalid.',
  SCREENPLAY_PLAN_INVALID: 'The returned plan or provenance failed the source-bound screenplay checks.',
  SCREENPLAY_SOURCE_INVALID: 'A selected image or source does not match its immutable original binding.',
  SCREENPLAY_NOTEBOOK_INVALID: 'The dedicated NotebookLM response could not be verified.',
  SCREENPLAY_RUNTIME_INVALID: 'Execution identities could not be verified. Accepted effects are retained.',
  SCREENPLAY_BODY_TOO_LARGE: 'Screenplay request bodies are limited to 256 KiB UTF-8.',
  SCREENPLAY_OPERATION_FAILED: 'The step failed. Fix its prerequisites before explicitly retrying.',
};
export interface ScreenplayUiError extends ScreenplayBlocker { uncertain: boolean }
export function screenplayError(error: unknown): ScreenplayUiError {
  const value = error as { code?: unknown; response?: { status?: number; data?: { error?: { code?: unknown } } } } | null;
  const rawCode = value?.response?.data?.error?.code;
  const known = typeof rawCode === 'string' && Object.prototype.hasOwnProperty.call(ERRORS, rawCode);
  const network = ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ECONNRESET', 'ERR_CANCELED'].includes(String(value?.code));
  const rejectedBeforeQueue = rawCode === 'SCREENPLAY_RUNTIME_UNAVAILABLE';
  const uncertain = network || (!rejectedBeforeQueue && (value?.response?.status ?? 0) >= 500)
    || rawCode === 'SCREENPLAY_EFFECT_UNCERTAIN' || rawCode === 'SCREENPLAY_WORKER_UNCERTAIN';
  return { code: known ? rawCode : 'SCREENPLAY_REQUEST_FAILED', uncertain,
    message: known ? ERRORS[rawCode] : network ? 'Connection lost. Observe the saved operation; do not resubmit an unknown request.'
      : !value?.response && error instanceof Error ? error.message.slice(0, 500)
        : 'The screenplay request could not be confirmed. Refresh the saved state.' };
}
export function screenplayReadError(error: unknown): ScreenplayUiError {
  const value = error as { code?: unknown; response?: unknown } | null;
  const timeout = ['ECONNABORTED', 'ETIMEDOUT'].includes(String(value?.code));
  if (timeout || ['ERR_NETWORK', 'ECONNRESET', 'ERR_CANCELED'].includes(String(value?.code))) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return { code: timeout ? 'SCREENPLAY_READ_TIMEOUT' : offline ? 'SCREENPLAY_READ_OFFLINE' : 'SCREENPLAY_READ_NETWORK', uncertain: true,
      message: timeout ? 'The screenplay read timed out. Refresh to retry.'
        : offline ? 'Studio is offline. Reconnect or refresh to read the screenplay.'
          : 'Connection to Studio was lost while reading. Refresh to retry.' };
  }
  const detail = screenplayError(error);
  return !value?.response || detail.code === 'SCREENPLAY_REQUEST_FAILED'
    ? { ...detail, message: 'The screenplay could not be read or verified. Refresh to retry.' } : detail;
}

export function screenplayNeedsAttention(record: ScreenplayRecord): boolean {
  return record.status === 'attention' || ['SCREENPLAY_EFFECT_UNCERTAIN', 'SCREENPLAY_WORKER_UNCERTAIN'].includes(record.blocker?.code ?? '');
}
export function screenplayEditable(record: ScreenplayRecord): boolean {
  return !screenplayNeedsAttention(record) && !record.execution && record.status !== 'running'
    && record.task?.kind !== 'run' && !['queued', 'running'].includes(record.task?.state ?? '');
}
export function screenplayCanResume(record: ScreenplayRecord): boolean {
  return !screenplayNeedsAttention(record) && !!record.task && ['paused', 'failed'].includes(record.task.state)
    && (record.task.kind !== 'run' || (!!record.planHash && record.approved?.planHash === record.planHash));
}
export function screenplayShouldPoll(record?: ScreenplayRecord | null, unconfirmed = false): boolean {
  return unconfirmed || !!record && (screenplayNeedsAttention(record)
    || ['queued', 'running', 'paused'].includes(record.task?.state ?? '') || record.status === 'running');
}
export type ScreenplayAction = 'analyze' | 'prepare' | 'approve' | 'run' | 'regenerate' | 'pause' | 'resume';
export interface ScreenplayOperation {
  id: string; action: ScreenplayAction; task?: Pick<ScreenplayTask, 'kind' | 'state' | 'step'>;
  planHash?: string; notebookId?: string; hadMatches: boolean; flowProjectId?: string; episodeNumbers?: number[];
  regeneration?: ScreenplayRegenerationSelection;
}
export function screenplayOperation(record: ScreenplayRecord, action: ScreenplayAction, flowProjectId?: string, episodeNumbers?: number[],
  regeneration?: ScreenplayRegenerationSelection): ScreenplayOperation {
  if (action === 'regenerate') requireValue(regeneration && UUID.test(regeneration.requestId)
    && validateEpisodeNumbers([regeneration.episodeNumber]) && validateSceneNumbers(regeneration.sceneNumbers), 'Choose the regeneration request and scenes.');
  return { id: record.id, action, ...(record.task ? { task: { kind: record.task.kind, state: record.task.state, step: record.task.step } } : {}),
    ...(record.planHash ? { planHash: record.planHash } : {}), ...(record.notebook ? { notebookId: record.notebook.notebookId } : {}),
    hadMatches: !!record.matches, ...(flowProjectId ? { flowProjectId } : {}),
    ...(action === 'run' && episodeNumbers !== undefined ? { episodeNumbers: validateEpisodeNumbers(episodeNumbers) } : {}),
    ...(action === 'regenerate' && regeneration ? { regeneration: { ...regeneration, sceneNumbers: validateSceneNumbers(regeneration.sceneNumbers) } } : {}) };
}
export function screenplayOperationObserved(operation: ScreenplayOperation, record: ScreenplayRecord): boolean {
  if (operation.id !== record.id) return false;
  const task = record.task, action = operation.action;
  if (action === 'regenerate') {
    const selected = operation.regeneration;
    if (!selected || !operation.planHash || operation.planHash !== record.planHash || !UUID.test(selected.requestId)) return false;
    const episode = record.execution?.episodes?.find(value => value.number === selected.episodeNumber);
    return [episode?.videoTake, ...(episode?.previousTakes ?? [])].some(take => take?.requestId === selected.requestId
      && take.sceneNumbers.length === selected.sceneNumbers.length && take.sceneNumbers.every((number, index) => number === selected.sceneNumbers[index]));
  }
  if (action === 'approve') return !!operation.planHash && record.approved?.planHash === operation.planHash
    && record.planHash === operation.planHash;
  if (action === 'pause') return !!task && task.kind === operation.task?.kind && task.state === 'paused'
    && operation.task.state !== 'paused';
  if (action === 'resume') return !!task && task.kind === operation.task?.kind
    && ['paused', 'failed'].includes(operation.task.state) && ['queued', 'running', 'complete'].includes(task.state);
  if (action === 'run' && ((operation.planHash && operation.planHash !== record.planHash)
    || (record.execution && (!UUID.test(record.execution.flowProjectId)
      || (operation.flowProjectId && record.execution.flowProjectId !== operation.flowProjectId))))) return false;
  if (action === 'run' && operation.episodeNumbers !== undefined && (!Array.isArray(operation.episodeNumbers)
    || !operation.episodeNumbers.length || !Array.isArray(record.runEpisodeNumbers)
    || !operation.episodeNumbers.every(number => Number.isInteger(number) && record.runEpisodeNumbers!.includes(number)))) return false;
  const changedTask = task?.kind === action && ['queued', 'running', 'paused', 'complete', 'failed'].includes(task.state)
    && (task.kind !== operation.task?.kind || task.state !== operation.task.state || task.step !== operation.task.step);
  if (changedTask) return true;
  if (action === 'analyze') return !operation.hadMatches && !!record.analysis && !!record.matches;
  if (action === 'prepare') return (!!record.notebook && record.notebook.notebookId !== operation.notebookId)
    || (!!record.planHash && record.planHash !== operation.planHash);
  return action === 'run' && !!record.execution && (!operation.flowProjectId || record.execution.flowProjectId === operation.flowProjectId);
}
export function screenplayRequestProgress(episode: ScreenplayEpisodeExecution | undefined, type: string) {
  const requests = (episode?.requests ?? []).filter(request => request.type === type && UUID.test(request.id)
    && (type !== 'GENERATE_VIDEO' || (request.modelFamily ?? 'veo') === (episode?.videoModel ?? 'veo')
      && (request.take ?? 1) === (episode?.videoTake?.number ?? 1)));
  const counts = { total: requests.length, completed: 0, active: 0, failed: 0, unknown: 0 };
  for (const request of requests) {
    if (request.status === 'COMPLETED') counts.completed++;
    else if (['PENDING', 'QUEUED', 'PROCESSING', 'RUNNING'].includes(request.status)) counts.active++;
    else if (['FAILED', 'CANCELLED'].includes(request.status)) counts.failed++;
    else counts.unknown++;
  }
  return counts;
}
export function screenplayReferenceDirectory(record: ScreenplayRecord): string | null {
  try {
    const folder = imageFolder(record.characterFolder).replace(/\/+$/u, '').replace(/\/{2,}/gu, '/');
    return `${folder}/screenplay-${uuid(record.id)}-${sha256(record.planHash!).slice(0, 12)}`;
  } catch { return null; }
}
function referenceEntity(record: ScreenplayRecord, reference: ScreenplayReferenceImage): ScreenplayEntity | undefined {
  const saved = record.execution?.entities?.find(entity => entity.id === reference.entityId);
  const selection = record.selections?.find(item => reference.originalAssetId && item.assetIds.includes(reference.originalAssetId));
  const product = record.plan?.products.find(item => item.name === selection?.name && selection.kind === 'product');
  const entity = record.plan?.entities.find(item => item.name === (saved?.name ?? product?.entityName) && !item.voiceOnly);
  if (!entity || saved && (saved.mediaId !== reference.mediaId || saved.name !== reference.entityName)) return;
  if (reference.source === 'generated') {
    if (!saved || saved.originalAssetId || reference.originalAssetId !== undefined
      || record.plan?.products.some(item => item.entityName === entity.name)
      || record.selections?.some(item => item.kind === 'character' && item.name === entity.name && item.assetIds.length)) return;
  } else if (reference.source === 'original') {
    const asset = record.assets?.find(item => item.id === reference.originalAssetId);
    if (!selection || !asset || !SHA256.test(asset.id) || asset.sha256 !== reference.sha256
      || asset.mime !== reference.mime || asset.size !== reference.size
      || (saved ? saved.originalAssetId !== asset.id || (product?.entityName ?? selection.name) !== entity.name
        : !product || record.execution?.entities?.some(item => item.originalAssetId === asset.id))) return;
  } else return;
  return entity;
}
export function screenplayStoredReferences(record: ScreenplayRecord): ScreenplayReferenceImage[] {
  if (!screenplayReferenceDirectory(record) || !Array.isArray(record.execution?.references) || record.execution.references.length > 80) return [];
  const directory = `screenplay-${record.id}-${record.planHash!.slice(0, 12)}/`;
  const ids = new Set<string>(), originals = new Set<string>();
  return record.execution.references.filter(reference => {
    if (!reference || !UUID.test(reference.entityId) || !UUID.test(reference.mediaId) || !SHA256.test(reference.sha256)
      || typeof reference.entityName !== 'string' || !reference.entityName.trim() || reference.entityName.length > 200
      || /[\p{Cc}\p{Cf}]/u.test(reference.entityName) || !Number.isSafeInteger(reference.size)
      || reference.size < 1 || reference.size > SCREENPLAY_IMAGE_BYTES || typeof reference.relativePath !== 'string'
      || !reference.relativePath.startsWith(directory)) return false;
    const extension = ({ 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' } as const)[reference.mime];
    const filename = reference.relativePath.slice(directory.length), suffix = `-${reference.entityId}.${extension}`;
    if (!extension || !filename.endsWith(suffix) || !/^[\p{L}\p{N}][\p{L}\p{N}_ -]{0,160}$/u.test(filename.slice(0, -suffix.length))
      || !referenceEntity(record, reference) || ids.has(reference.entityId)
      || reference.originalAssetId && originals.has(reference.originalAssetId)) return false;
    ids.add(reference.entityId); if (reference.originalAssetId) originals.add(reference.originalAssetId);
    return true;
  });
}
export function screenplayReferenceProgress(record: ScreenplayRecord) {
  const canonical = record.plan?.entities.filter(entity => !entity.voiceOnly) ?? [], stored = screenplayStoredReferences(record);
  const expected = canonical.reduce((total, entity) => total + Math.max(1, entity.sourceEvidenceIds?.length ?? 0), 0);
  const completed = canonical.filter(entity => record.execution?.entities?.some(saved => saved.name === entity.name
    && stored.some(reference => reference.entityId === saved.id && reference.mediaId === saved.mediaId))).length;
  const verified = expected > 0 && completed === canonical.length && stored.length === expected
    && record.execution?.references?.length === expected && record.execution.referenceDirectory === screenplayReferenceDirectory(record)
    && canonical.every(entity => stored.filter(reference => referenceEntity(record, reference)?.name === entity.name).length
      === Math.max(1, entity.sourceEvidenceIds?.length ?? 0));
  return { total: canonical.length, completed, stored: stored.length, expected, verified };
}
export function screenplayEpisodeOutput(record: ScreenplayRecord, number: number): ScreenplayOutput | null {
  const planned = record.plan?.episodes.find(episode => episode.number === number);
  const output = record.outputs?.find(episode => episode.episode === number)
    ?? record.execution?.episodes?.find(episode => episode.number === number)?.output;
  if (!planned || !output || output.durationSec < 30 || output.durationSec > 45 || output.durationSec !== planned.durationSec
    || output.clipCount !== planned.scenes.length || !UUID.test(output.opencutProjectId) || !UUID.test(output.opencutRenderId)
    || !/^\/flowkit-media\/[a-f0-9]{64}\.mp4$/.test(output.mergedUrl) || !safeFilmMediaUrl(output.mergedUrl)) return null;
  return { ...output, episode: number };
}
export function screenplayPreviousOutputs(record: ScreenplayRecord, number: number): (ScreenplayOutput & { take: number })[] {
  const history = record.execution?.episodes?.find(episode => episode.number === number)?.previousTakes;
  if (!Array.isArray(history) || history.length > 7) return [];
  return history.flatMap((take, index) => {
    if (take?.number !== index + 1 || !take.output) return [];
    const output = screenplayEpisodeOutput({ ...record, outputs: [{ episode: number, ...take.output }] }, number);
    return output ? [{ take: take.number, ...output }] : [];
  });
}
export function screenplayRegenerationReason(record: ScreenplayRecord, policy: ScreenplayPolicy | null | undefined,
  episodeNumber: number, sceneNumbers: number[]): string {
  const blocked = screenplayActionReason(record, 'regenerate', policy);
  if (blocked) return blocked;
  const episode = record.execution?.episodes?.find(value => value.number === episodeNumber);
  if (!episode || !screenplayEpisodeOutput(record, episodeNumber)
    || record.runEpisodeNumbers && !record.runEpisodeNumbers.includes(episodeNumber)) return 'A completed episode export is required.';
  if ((episode.videoTake?.number ?? 1) >= 8) return ERRORS.SCREENPLAY_TAKE_LIMIT;
  try {
    const selected = validateSceneNumbers(sceneNumbers), planned = record.plan!.episodes.find(value => value.number === episodeNumber)!;
    if (!selected.every(number => planned.scenes.some(scene => scene.number === number))) return ERRORS.SCREENPLAY_SCENE_SELECTION_INVALID;
  } catch { return ERRORS.SCREENPLAY_SCENE_SELECTION_INVALID; }
  return '';
}

export function screenplayVoiceWords(description?: string): number {
  return typeof description === 'string' && description.trim() ? description.trim().split(/\s+/u).length : 0;
}
export function screenplayPlanReady(record: ScreenplayRecord, policy?: ScreenplayPolicy | null): boolean {
  try {
    const { plan, provenance: proof, notebook, analysis, bindings, loadedSkills } = record;
    const skills = ['fk-create-project', 'fk-gen-refs', 'fk-gen-images', 'fk-gen-videos', 'fk-pipeline'];
    const planner = record.planner ?? 'gemini';
        if (record.directionProfile !== plan?.directionProfile || record.directionProfile !== undefined && record.directionProfile !== 'cinematic-luxury-v1') return false;
        if (record.directionProfile) {
          const validReceipt = (receipt: CinematicSkillReceipt | undefined, provider: string) => {
            if (!receipt || receipt.profile !== record.directionProfile || receipt.version !== 1
              || receipt.provider !== provider || !SHA256.test(receipt.sha256) || !Array.isArray(receipt.documents)) return false;
            const expected = receipt.documents.length === 4
              ? ['film-flowkit-production', 'studio-cinematic-direction', 'luxury-drama', provider]
              : ['studio-cinematic-direction', 'luxury-drama', provider];
            return receipt.documents.length === expected.length
              && receipt.documents.every((document, index) => document.id === expected[index] && SHA256.test(document.sha256));
          };
          const sharedCount = (record.cinematicSkills?.documents.length ?? 0) - 1;
          const sharedIndices = sharedCount > 0 ? Array.from({ length: sharedCount }, (_, i) => i) : [];
          const provenanceProvider = proof?.cinematicSkills?.provider;
          if (!['2026-09-18.cinematic.1', '2026-09-21.cinematic.1', '2026-10-05.cinematic.2', '2026-10-05.cinematic.3'].includes(policy?.version ?? '') || !validReceipt(record.cinematicSkills, planner)
            || !provenanceProvider || ![planner, 'notebooklm'].includes(provenanceProvider)
            || !validReceipt(proof?.cinematicSkills, provenanceProvider) || !sharedIndices.length || !sharedIndices.every(index => record.cinematicSkills!.documents[index].sha256
              === proof!.cinematicSkills!.documents[index].sha256)) return false;
          if (!plan?.episodes.every(episode => episode.scenes.every(scene => {
            const direction = scene.cinematicDirection;
            return direction?.sceneNumber === scene.number && direction.generationMode === (policy?.version === '2026-10-05.cinematic.3' ? 'first-last' : 'first-frame')
              && direction.startFramePrompt === scene.prompt && typeof direction.endFramePrompt === 'string'
              && typeof direction.transitionIn === 'string' && typeof direction.transitionOut === 'string'
              && Array.isArray(direction.shots) && direction.shots.length >= (policy?.version === '2026-10-05.cinematic.3' ? 1 : 2) && direction.shots.length <= (policy?.version === '2026-10-05.cinematic.3' ? 3 : 4)
              && direction.shots.every((shot, index) => Number.isInteger(shot.startMs) && Number.isInteger(shot.endMs)
                && shot.startMs === (index ? direction.shots[index - 1].endMs : 0) && shot.endMs > shot.startMs)
              && direction.shots[direction.shots.length - 1].endMs === Math.round(scene.durationSec * 1000)
              && Array.isArray(direction.dialogueCues) && direction.dialogueCues.length === scene.dialogue.length
              && direction.dialogueCues.every((cue, index) => cue.dialogueIndex === index && Number.isInteger(cue.startMs)
                && Number.isInteger(cue.endMs) && cue.startMs >= 0 && cue.endMs > cue.startMs && cue.endMs <= scene.durationSec * 1000);
          }))) return false;
        }
    if (!policy || !plan || !proof || !analysis || !bindings || !loadedSkills || !notebook?.verified
      || !['gemini', 'codex'].includes(planner) || (record.analysisProvider === undefined ? planner !== 'gemini' : record.analysisProvider !== planner)
      || !SHA256.test(record.planHash ?? '') || plan.version !== 2 || !screenplaySelectionsComplete(record)
      || notebook.policyVersion !== policy.version || notebook.policySha256 !== policy.sha256
      || proof.policyVersion !== policy.version || proof.policySha256 !== policy.sha256
      || plan.audit.policyVersion !== policy.version || plan.audit.policySha256 !== policy.sha256
      || proof.notebookId !== notebook.notebookId || proof.rulesSourceId !== notebook.rulesSourceId
      || ![proof.notebookId, proof.rulesSourceId, proof.contextSourceId, proof.conversationId].every(value => UUID.test(value))
      || !SHA256.test(proof.draftSha256) || proof.draftSha256 !== plan.draftSha256
      || loadedSkills.length !== skills.length || new Set(loadedSkills.map(skill => skill.id)).size !== skills.length
      || !loadedSkills.every(skill => skills.includes(skill.id) && SHA256.test(skill.sha256))
      || plan.audit.blockingFindings.length || plan.audit.rules.length !== policy.ruleIds.length
      || !policy.ruleIds.every(id => plan.audit.rules.filter(rule => rule.id === id && rule.status === 'PASS').length === 1)) return false;
    if (bindings.length !== record.selections!.length || !record.selections!.every(selected => bindings.filter(binding =>
      binding.name === selected.name && binding.kind === selected.kind && binding.generate === selected.generate
      && (selected.generate === true ? binding.sourceIds.length === 0 : binding.sourceIds.length >= 1
        && binding.sourceIds.length <= (binding.kind === 'product' ? 6 : 1))).length === 1)) return false;
    const sources = [proof.rulesSourceId, proof.contextSourceId, ...bindings.flatMap(binding => binding.sourceIds)];
    if (!sources.every(value => UUID.test(value)) || new Set(sources).size !== sources.length
      || proof.sourceIds.length !== sources.length || new Set(proof.sourceIds).size !== sources.length
      || !proof.sourceIds.every(source => sources.includes(source))
      || !plan.entities.every(entity => entity.sourceEvidenceIds.every(source => sources.includes(source)))) return false;
    const voiced = (entity: ScreenplayEntity) => ['character', 'creature'].includes(entity.entityType)
      && screenplayVoiceWords(entity.voiceDescription) >= 1 && screenplayVoiceWords(entity.voiceDescription) <= 30;
    if (!plan.entities.every(entity => entity.voiceDescription === undefined ? !entity.voiceOnly : voiced(entity))) return false;
    if (!plan.episodes.length || plan.episodes.length > 8 || plan.episodes.length !== analysis.episodes.length
      || plan.products.length !== analysis.products.length
      || new Set(plan.products.map(product => product.name)).size !== plan.products.length
      || !analysis.products.every(product => plan.products.some(planned => planned.name === product.name
        && plan.entities.some(entity => entity.name === planned.entityName)))) return false;
    return plan.episodes.every((episode, index) => {
      const original = analysis.episodes[index], assigned = screenplayEpisodeProductNames(episode);
      const products = plan.products.filter(product => assigned.includes(product.name));
      return episode.number === index + 1 && original.number === episode.number && assigned.length > 0 && products.length === assigned.length
        && JSON.stringify(screenplayEpisodeProductNames(original)) === JSON.stringify(assigned) && episode.durationSec === original.durationSec
        && episode.durationSec >= 30 && episode.durationSec <= 45
        && analysis.products.filter(item => item.episodes.includes(episode.number)).length === assigned.length
        && assigned.every(name => analysis.products.some(item => item.name === name && item.episodes.includes(episode.number)))
        && products.every(product => episode.scenes.some(scene => scene.entityNames.includes(product.entityName)))
        && episode.scenes.length >= 4 && episode.scenes.length <= 12 && episode.scenes.every(scene => (policy.version === '2026-10-05.cinematic.3' ? [4, 6, 8].includes(scene.sourceDurationSec) : scene.sourceDurationSec === 8)
          && Number.isFinite(scene.durationSec) && scene.durationSec >= 1 && scene.durationSec <= 8
          && Math.abs(scene.durationSec * 1000 - Math.round(scene.durationSec * 1000)) < 1e-7
          && scene.entityNames.every(name => plan.entities.some(entity => entity.name === name))
          && scene.dialogue.every(line => plan.entities.some(entity => entity.name === line.speaker && voiced(entity)))
          && !plan.products.some(other => !assigned.includes(other.name) && scene.entityNames.includes(other.entityName)))
        && episode.scenes.reduce((total, scene) => total + Math.round(scene.durationSec * 1000), 0) === episode.durationSec * 1000;
    });
  } catch { return false; }
}
export function screenplayActionReason(record: ScreenplayRecord, action: ScreenplayAction,
  policy?: ScreenplayPolicy | null, flowProjectId?: string, episodeNumbers?: number[]): string {
  if (screenplayNeedsAttention(record)) return 'Outcome uncertain. Operator inspection is required; do not resubmit.';
  if (record.directionProfile && record.plan?.audit.policyVersion !== '2026-10-05.cinematic.3'
    && (['run', 'regenerate'].includes(action) || action === 'resume' && record.task?.kind === 'run'))
    return 'Create a revised draft and approve a detailed plan with first + end frames before generating new clips.';
  if (action === 'pause') return record.task && ['queued', 'running'].includes(record.task.state) ? '' : 'No active task to pause.';
  if (action === 'resume') return !screenplayCanResume(record) ? 'No safely resumable task.'
    : record.task?.kind === 'run' && !screenplayPlanReady(record, policy) ? 'Current plan provenance and policy are required.' : '';
  if (['queued', 'running', 'paused'].includes(record.task?.state ?? '')) return 'Current work must finish or resume first.';
  if (action === 'analyze') return !screenplayEditable(record) ? 'The screenplay is locked.'
    : record.matches ? 'The current inputs already have an analysis. Change and save inputs to analyze again.' : '';
  const stalePolicy = !!policy && !!record.notebook && !!record.provenance && !!record.plan
    && [record.notebook, record.provenance, record.plan.audit].some(saved => saved.policyVersion !== policy.version
      || saved.policySha256 !== policy.sha256);
  if (action === 'prepare') return stalePolicy && !record.execution && screenplayEditable(record) ? ''
    : !screenplayEditable(record) ? 'The screenplay is locked.'
    : !screenplaySelectionsComplete(record) ? 'Select every product and available cast original, or generate missing visible cast.'
      : record.plan ? 'A prepared plan is already saved.' : '';
  if (stalePolicy) return 'The approved plan policy changed. Re-prepare and approve the plan against the current policy before generating.';
  if (!screenplayPlanReady(record, policy)) return 'A verified plan matching current policy, originals and episode timings is required.';
  if (action === 'approve') return record.status !== 'review' || record.approved?.planHash === record.planHash
    ? 'The current plan is already approved or not awaiting review.' : '';
  const completedBatch = record.status === 'complete' && record.task?.kind === 'run'
    && record.task.state === 'complete' && !!record.execution;
  if (action === 'regenerate') return completedBatch && record.approved?.planHash === record.planHash ? ''
    : 'A completed approved batch is required before regeneration.';
  if ((record.status !== 'approved' && !completedBatch) || record.approved?.planHash !== record.planHash
    || (record.execution && !completedBatch)) return 'Approve the current plan before running.';
  try {
    const selected = validateEpisodeNumbers(episodeNumbers === undefined ? record.plan!.episodes.filter(episode =>
      !record.outputs?.some(output => output.episode === episode.number)).map(episode => episode.number) : episodeNumbers);
    if (!selected.every(number => record.plan!.episodes.some(episode => episode.number === number))) return 'Select episodes from the approved plan.';
    if (selected.some(number => record.outputs?.some(output => output.episode === number))) return 'Selected episodes have already completed.';
  } catch { return 'Select one or more distinct episodes from the approved plan.'; }
  return flowProjectId === undefined || parseFlowProjectId(flowProjectId) ? '' : 'Enter a valid Google Flow project UUID.';
}

export type ScreenplayHttpClient = Pick<typeof api, 'get' | 'post' | 'put'>;
export function createScreenplaysApi(client: ScreenplayHttpClient = api) {
  const options = (signal?: AbortSignal) => ({ signal, timeout: SCREENPLAY_TIMEOUT });
  const imageQueue: { start: () => void }[] = [];
  let activeImages = 0;
  const pumpImages = () => {
    while (activeImages < 2 && imageQueue.length) imageQueue.shift()!.start();
  };
  const image = (path: string, signal?: AbortSignal): Promise<Blob> => new Promise((resolve, reject) => {
    const cancelled = () => Object.assign(new Error('Image read cancelled.'), { code: 'ERR_CANCELED' });
    if (signal?.aborted) { reject(cancelled()); return; }
    if (imageQueue.length >= 256) { reject(new Error('Too many queued image reads.')); return; }
    const pending = { start: () => {
      signal?.removeEventListener('abort', abort);
      if (signal?.aborted) { reject(cancelled()); return; }
      activeImages++;
      void (async () => {
        try { resolve((await client.get<Blob>(path, { ...options(signal), responseType: 'blob' })).data); }
        catch (error) { reject(error); }
        finally { activeImages--; pumpImages(); }
      })();
    } };
    const abort = () => {
      const index = imageQueue.indexOf(pending);
      if (index >= 0) imageQueue.splice(index, 1);
      signal?.removeEventListener('abort', abort);
      reject(cancelled());
    };
    signal?.addEventListener('abort', abort, { once: true });
    imageQueue.push(pending); pumpImages();
  });
  const post = async (id: string, action: string, body: unknown, signal?: AbortSignal) =>
    (await client.post<ScreenplayRecord>(`${screenplayPath(id)}/${action}`, boundedBody(body), options(signal))).data;
  return {
    async list(signal?: AbortSignal) {
      return (await client.get<{ films: ScreenplaySummary[] }>('/films/screenplays', options(signal))).data;
    },
    async policy(signal?: AbortSignal, directionProfile?: ScreenplayDirectionProfile, version?: string) {
      requireValue(directionProfile === undefined || directionProfile === 'cinematic-luxury-v1', 'Unknown cinematic direction profile.');
      requireValue(version === undefined || (directionProfile ? ['2026-09-18.cinematic.1', '2026-09-21.cinematic.1', '2026-10-05.cinematic.2', '2026-10-05.cinematic.3']
        : ['2026-09-15.1', '2026-09-21.1']).includes(version), 'Unknown screenplay policy version.');
      return (await client.get<ScreenplayPolicy>('/films/screenplays/policy', { ...options(signal),
        ...(directionProfile || version ? { params: { ...(directionProfile ? { directionProfile } : {}), ...(version ? { version } : {}) } } : {}) })).data;
    },
    async get(id: string, signal?: AbortSignal) {
      return (await client.get<ScreenplayRecord>(screenplayPath(id), options(signal))).data;
    },
    async regenerate(id: string, command: ScreenplayRegenerationCommand, signal?: AbortSignal) {
      return post(id, 'regenerate', validateScreenplayRegeneration(command), signal);
    },
    async create(input: ScreenplayCreateInput, signal?: AbortSignal) {
      return (await client.post<ScreenplayRecord>('/films/screenplays',
        boundedBody({ id: uuid(input.id), ...validateScreenplayInput(input) }), options(signal))).data;
    },
    async updateInput(id: string, input: ScreenplayInput, signal?: AbortSignal) {
      return (await client.put<ScreenplayRecord>(`${screenplayPath(id)}/input`, validateScreenplayInput(input), options(signal))).data;
    },
    async setSelections(record: ScreenplayRecord, selections: ScreenplaySelection[], signal?: AbortSignal) {
      return (await client.put<ScreenplayRecord>(`${screenplayPath(record.id)}/selections`,
        { selections: validateScreenplaySelections(record, selections) }, options(signal))).data;
    },
    async original(id: string, assetId: string, signal?: AbortSignal) {
      return image(`${screenplayPath(id)}/assets/${sha256(assetId)}`, signal);
    },
    async referenceImage(id: string, entityId: string, signal?: AbortSignal) {
      return image(`${screenplayPath(id)}/references/${uuid(entityId)}`, signal);
    },
    analyze: (id: string, signal?: AbortSignal) => post(id, 'analyze', {}, signal),
    prepare: (id: string, signal?: AbortSignal, replaceStalePlan = false) => post(id, 'prepare', replaceStalePlan ? { replaceStalePlan: true } : {}, signal),
    async approve(id: string, planHash: string, signal?: AbortSignal) { return post(id, 'approve', { planHash: sha256(planHash) }, signal); },
    async run(id: string, planHash: string, flowProjectId?: string, signal?: AbortSignal, episodeNumbers?: number[]) {
      const projectId = typeof flowProjectId === 'string' ? parseFlowProjectId(flowProjectId) : null;
      requireValue(flowProjectId === undefined || projectId, 'Enter a valid Google Flow project UUID.');
      return post(id, 'run', { planHash: sha256(planHash), ...(projectId ? { flowProjectId: projectId } : {}),
        ...(episodeNumbers !== undefined ? { episodeNumbers: validateEpisodeNumbers(episodeNumbers) } : {}) }, signal);
    },
    pause: (id: string, signal?: AbortSignal) => post(id, 'pause', {}, signal),
    resume: (id: string, signal?: AbortSignal) => post(id, 'resume', {}, signal),
  };
}
export const screenplaysApi = createScreenplaysApi();
